import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import useCurrency from '@src/hooks/useCurrency';
import { isAddToCartButton, isRetailerOnly, RETAILER_ONLY_LABEL } from '@src/utilities/retailerOnly';

type ButtonProps = {
  settings?: any;
  icon?: string;
  type?: 'button' | 'submit' | 'reset' | undefined;
  style?: any;
  size?: any;
  styleType?: any;
  text?: boolean;
  label?: string;
  link?: string;
  className?: string;
  reverse?: boolean;
  disabled?: boolean;
  buttonStyle?: any;
  onClick?: Function;
  fullWidth?: boolean;
  loadingText?: string;
  isLoading?: boolean;
  gap?: number;
};

export function Button({
  settings = {},
  icon,
  type = 'button',
  style = 'primary',
  styleType = 'button',
  size = '',
  text = true,
  link,
  className,
  reverse,
  label,
  disabled = false,
  buttonStyle = {},
  onClick,
  fullWidth = false,
  loadingText = 'Loading...',
  isLoading = false,
  gap = 8
}: ButtonProps) {
  const [triggerColors, setTriggerColors] = useState<{ activeColor: string; inactiveColor: string } | null>(null);
  const [isTargetActive, setIsTargetActive] = useState(true);
  const elementRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const { currentCurrencySymbol } = useCurrency();

  // Get product context and match variants
  const productContext = (window as any).getProductContext?.();
  let variantId = null;
  let matchedVariant: any = null;

  if (productContext && productContext.variantId) {
    variantId = productContext.variantId;
  }

  // Loop through settings.product.variants and match against product context
  if (settings.product && settings.product.variants && productContext) {
    settings.product.variants.forEach(variant => {
      // Match by variant ID
      if (variant.id === variantId || variant.id === productContext.variantId) {
        matchedVariant = variant;
      }
      
      // Also match by current product variants if available
      if (productContext.variants) {
        const contextVariant = productContext.variants.find(v => v.id === variant.id);
        if (contextVariant) {
          matchedVariant = { ...variant, ...contextVariant };
        }
      }
    });
  }
  
  const retailerOnly =
    isAddToCartButton(settings?.block_settings?.button_classes || className) && isRetailerOnly(settings.product);
  const soldOut = settings?.block_settings?.disable_button_product && matchedVariant && matchedVariant.available === false;
  const buttonLabel = retailerOnly ? RETAILER_ONLY_LABEL : soldOut ? 'Out of stock' : label;

  // Listen for trigger color events
  useEffect(() => {
    const handleTriggerColorChange = (event: any) => {
      const { state, triggerSelector, colors } = event.detail || {};

      // Check if this button's parent wrapper matches the CSS selector
      // The ID and classes are applied to parent .button-block
      if (triggerSelector && elementRef.current) {
        try {
          const parentElement = elementRef.current.closest('.button-block');

          if (parentElement && parentElement.matches(triggerSelector)) {
            setTriggerColors(colors);
            setIsTargetActive(state === 'visible');
          }
        } catch (error) {
          console.warn('Invalid CSS selector for trigger color:', triggerSelector);
        }
      }
    };

    window.addEventListener('trigger-color-change', handleTriggerColorChange);

    return () => {
      window.removeEventListener('trigger-color-change', handleTriggerColorChange);
    };
  }, []);

  function handleClick(e) {
    if (retailerOnly) {
      e.preventDefault();
      return;
    }
    if (onClick) onClick(e);
  }

  // Apply trigger colors based on target state
  const appliedButtonStyle = React.useMemo(() => {
    const baseStyle = { 
      ...buttonStyle,
      minWidth: settings?.block_settings?.min_width ? `${settings.block_settings.min_width}px` : null
    };

    if (triggerColors) {
      if (isTargetActive) {
        baseStyle.color = triggerColors.activeColor;
        baseStyle['--button-color'] = triggerColors.activeColor;
      } else {
        baseStyle.color = triggerColors.inactiveColor;
        baseStyle['--button-color'] = triggerColors.inactiveColor;
      }
    }

    return baseStyle;
  }, [buttonStyle, triggerColors, isTargetActive]);

  // Calculate full width classes based on responsive settings
  const getFullWidthClasses = () => {
    const blockSettings = settings?.block_settings;
    
    if (blockSettings?.button_full_width_enable_responsive) {
      const classes: string[] = [];
      
      if (blockSettings.button_full_width_mobile) {
        classes.push('w-full');
      }
      if (blockSettings.button_full_width_tablet) {
        classes.push('md:w-full');
      }
      if (blockSettings.button_full_width_desktop) {
        classes.push('lg:w-full');
      }
      if (blockSettings.button_full_width_ultrawide) {
        classes.push('xl:w-full');
      }
      
      // If no device-specific settings are enabled, default to fit-content
      if (!blockSettings.button_full_width_mobile && 
          !blockSettings.button_full_width_tablet && 
          !blockSettings.button_full_width_desktop && 
          !blockSettings.button_full_width_ultrawide) {
        classes.push('w-fit');
      }
      
      return classes.join(' ');
    }
    
    // Fall back to original logic for backward compatibility
    return fullWidth || blockSettings?.button_full_width ? 'w-full' : '';
  };

  // Common props shared between button and link
  const commonProps = {
    className: clsx(
     `${styleType === 'link' ? `link link-${style}` : `button`} ${
        size ? `${styleType === 'link' ? 'link' : 'button'}-${size} ` : ''
      } ${className}`,
      getFullWidthClasses(),
      {
        [`button-${style}`]: settings?.block_settings?.button_style == 'global' && styleType !== 'link',
        reverse: reverse,
        'icon-only': icon && !label && !isLoading,
        'text-only': style === 'text' || style === 'dark-text',
        'cursor-not-allowed': isLoading,
        'flex items-center justify-center': isLoading
      }
    ),
    style: appliedButtonStyle,
    ref: elementRef
  };

  const iconSize = settings?.block_settings?.button_icon_size_mobile || settings?.block_settings?.button_icon_size;
  const iconClass = clsx([`icon-${icon}`, 'button__icon', 'no-underline', {
    [`text-[${iconSize}px]`]: iconSize,
    [`lg:text-[${settings?.block_settings?.button_icon_size}px]`]: settings?.block_settings?.button_icon_size,
  }]);

  const image = settings?.block_settings?.image;
  const imageSize = settings?.block_settings?.button_image_size;
  const imageContent = image ? (
    <img 
      src={image} 
      alt={label} 
      className='button__image'
      style={{ width: imageSize ? `${imageSize}px` : 'auto', height: 'auto' }}
    />
  ) : null;

  // If both icon and image are set, prioritize image
  const finalIcon = imageContent || (icon ? <span className={iconClass}></span> : null);

  // Common content shared between button and link
  const content = (
    <>
      {isLoading ? (
        <>
          <div className='animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-current border-t-transparent rounded-full'></div>
          {loadingText}
        </>
      ) : icon || image ? (
        <div
          className='flex items-center justify-between w-full'
          style={{ gap: `${gap}px` }}
        >
          {reverse ? (
            <>
              {text && label && (
                <span className='button__label'>{buttonLabel}</span>
              )}
              {finalIcon}
            </>
          ) : (
            <>
              {finalIcon}
              {text && label && (
                <span className='button__label'>{buttonLabel}</span>
              )}
            </>
          )}
        </div>
      ) : (
        text && label && (
          <span className='button__label'>{buttonLabel}</span>
        )
      )}
    </>
  );
  // For links, we always render an <a> tag with type attribute
  if (link) {
    return (
      <a
        href={link}
        title={label}
        onClick={(e) => handleClick(e)}
        {...commonProps}
        className={`${commonProps.className} ${
          disabled || isLoading || retailerOnly ? 'cursor-not-allowed disabled pointer-events-none' : ''
        }`}
      >
        {content}
      </a>
    );
  }

  // For regular buttons, respect the type attribute (submit, reset, button)
  return (
    <button
      type={type}
      title={label}
      disabled={disabled || isLoading || retailerOnly || (settings.product && soldOut)}
      onClick={(e) => handleClick(e)}
      {...commonProps}
    >
      {content}
      {settings?.block_settings?.show_price && settings.product && !retailerOnly && !(matchedVariant && matchedVariant.available === false) && (
        <span>
          {currentCurrencySymbol}
          {matchedVariant 
            ? (parseInt(matchedVariant.price) / 100).toFixed(2)
            : (parseInt(settings.product.price) / 100).toFixed(2)
          }
        </span>
      )}
    </button>
  );
}